import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, CreditCard, Landmark, Save, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const inputCls = 'w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]';
const emptyBank = { bank_name: '', account_name: '', iban: '', branch: '', description: '', is_active: true, position: 0 };

// PayTR callback (bildirim) URL must hit the BACKEND (api.<domain>), not the frontend.
// Derive it from the current host by stripping a leading www/panel/app/portal and prefixing "api.".
function paytrCallbackUrl() {
  try {
    const host = window.location.hostname;
    // localhost / IP / preview → fall back to backend base if available
    if (host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host) || host.endsWith('.emergentagent.com')) {
      const be = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, '');
      return `${be}/api/paytr/callback`;
    }
    const parts = host.split('.');
    const lead = parts[0].toLowerCase();
    const baseParts = ['www', 'panel', 'app', 'portal'].includes(lead) ? parts.slice(1) : parts;
    return `https://api.${baseParts.join('.')}/api/paytr/callback`;
  } catch {
    return `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/paytr/callback`;
  }
}

function Toggle({ checked, onChange, label, hint, testid }) {
  return (
    <label className="flex items-start justify-between gap-4 p-3 rounded-lg border border-border cursor-pointer hover:bg-secondary/50 transition-colors">
      <span>
        <span className="block text-sm font-medium">{label}</span>
        {hint && <span className="block text-xs text-muted-foreground mt-0.5">{hint}</span>}
      </span>
      <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} data-testid={testid}
        className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${checked ? 'bg-[hsl(var(--gold))]' : 'bg-secondary border border-border'}`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
      </button>
    </label>
  );
}

export default function PaymentSettings() {
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showKeys, setShowKeys] = useState(false);

  const [banks, setBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(true);
  const [showBankForm, setShowBankForm] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [bankForm, setBankForm] = useState(emptyBank);
  const [bankSubmitting, setBankSubmitting] = useState(false);

  const loadBanks = useCallback(() => {
    setLoadingBanks(true);
    api.get('/admin/bank-accounts').then(r => setBanks(r.data)).catch(() => {}).finally(() => setLoadingBanks(false));
  }, []);

  useEffect(() => {
    api.get('/admin/payment-settings').then(r => setSettings(r.data)).catch(() => toast.error('Ayarlar yüklenemedi'));
    loadBanks();
  }, [loadBanks]);

  const setS = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  const saveSettings = async () => {
    setSavingSettings(true);
    try { await api.put('/admin/payment-settings', settings); toast.success('Ödeme ayarları kaydedildi'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Kaydetme başarısız'); }
    finally { setSavingSettings(false); }
  };

  const resetBank = () => { setBankForm(emptyBank); setEditingBank(null); setShowBankForm(false); };
  const editBank = (b) => {
    setEditingBank(b);
    setBankForm({ bank_name: b.bank_name || '', account_name: b.account_name || '', iban: b.iban || '', branch: b.branch || '', description: b.description || '', is_active: b.is_active === true || b.is_active === 'true' || b.is_active === 'True', position: parseInt(b.position) || 0 });
    setShowBankForm(true);
  };
  const submitBank = async (e) => {
    e.preventDefault();
    if (!bankForm.bank_name.trim() || !bankForm.iban.trim()) { toast.error('Banka adı ve IBAN gerekli'); return; }
    setBankSubmitting(true);
    try {
      const payload = { ...bankForm, position: parseInt(bankForm.position) || 0 };
      if (editingBank) { await api.put(`/admin/bank-accounts/${editingBank.id}`, payload); toast.success('Banka hesabı güncellendi'); }
      else { await api.post('/admin/bank-accounts', payload); toast.success('Banka hesabı eklendi'); }
      resetBank(); loadBanks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata oluştu'); }
    finally { setBankSubmitting(false); }
  };
  const deleteBank = async (id) => {
    if (!window.confirm('Bu banka hesabını silmek istediğinize emin misiniz?')) return;
    try { await api.delete(`/admin/bank-accounts/${id}`); toast.success('Silindi'); loadBanks(); }
    catch { toast.error('Silme başarısız'); }
  };

  if (!settings) {
    return <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="admin-payment-settings" className="space-y-8 max-w-3xl">
      <div>
        <h1 className="font-heading text-2xl font-semibold">Ödeme Ayarları</h1>
        <p className="text-sm text-muted-foreground mt-0.5">PayTR kredi kartı ve havale/EFT seçeneklerini bu siteye özel yönetin.</p>
      </div>

      {/* General + PayTR */}
      <section className="p-6 rounded-2xl bg-card border border-border space-y-5">
        <div className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-[hsl(var(--gold))]" />
          <h2 className="font-heading text-lg font-semibold">Genel & PayTR</h2>
        </div>

        <div className="p-3 rounded-lg bg-[hsl(var(--gold)/0.08)] border border-[hsl(var(--gold)/0.3)] text-xs text-muted-foreground">
          Bu modülün genel açma/kapama anahtarı <strong>Süper Admin → Tenant Yönetimi</strong> ekranındaki
          “Ödeme &amp; Ürün Yönetimi Aktif” seçeneğiyle kontrol edilir. Buradan yalnızca ödeme yöntemlerini ve PayTR bilgilerini yapılandırırsınız.
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Toggle label="Havale / EFT aktif" hint="Banka havalesi ile ödeme." checked={!!settings.bank_transfer_enabled} onChange={v => setS('bank_transfer_enabled', v)} testid="toggle-wire" />
          <Toggle label="PayTR (Kredi Kartı) aktif" hint="Kredi kartı ile ödeme." checked={!!settings.paytr_enabled} onChange={v => setS('paytr_enabled', v)} testid="toggle-paytr" />
          <Toggle label="PayTR Test Modu" hint="Açıkken test kartları kullanılır." checked={!!settings.paytr_test_mode} onChange={v => setS('paytr_test_mode', v)} testid="toggle-paytr-test" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1.5">PayTR Yöntemi</label>
            <select value={settings.paytr_mode} onChange={e => setS('paytr_mode', e.target.value)} className={inputCls} data-testid="paytr-mode">
              <option value="iframe">iFrame (PayTR ödeme ekranı)</option>
              <option value="direct">Direct API (kart formu sitede)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Para Birimi</label>
            <select value={settings.currency} onChange={e => setS('currency', e.target.value)} className={inputCls} data-testid="paytr-currency">
              <option value="TL">TL</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>
          </div>
        </div>

        <div className="border-t border-border pt-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium">PayTR Mağaza Bilgileri</span>
            <button type="button" onClick={() => setShowKeys(v => !v)} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
              {showKeys ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {showKeys ? 'Gizle' : 'Göster'}
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Merchant ID</label>
              <input value={settings.paytr_merchant_id} onChange={e => setS('paytr_merchant_id', e.target.value)} className={inputCls} data-testid="paytr-merchant-id" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Merchant Key</label>
              <input type={showKeys ? 'text' : 'password'} value={settings.paytr_merchant_key} onChange={e => setS('paytr_merchant_key', e.target.value)} className={inputCls} data-testid="paytr-merchant-key" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Merchant Salt</label>
              <input type={showKeys ? 'text' : 'password'} value={settings.paytr_merchant_salt} onChange={e => setS('paytr_merchant_salt', e.target.value)} className={inputCls} data-testid="paytr-merchant-salt" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Bildirim (Callback) URL'nizi PayTR Mağaza Panelinde şu adres olarak tanımlayın:
            <code className="ml-1 px-1.5 py-0.5 rounded bg-secondary text-foreground break-all">{paytrCallbackUrl()}</code>
          </p>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5">
            Not: <code className="px-1 rounded bg-secondary">api.</code> alt alan adınızın backend sunucunuza (Railway) "Custom Domain" olarak eklenmiş ve yönlendirilmiş olması gerekir. Aksi halde bildirim başarısız olur.
          </p>
        </div>

        <div className="flex justify-end">
          <button onClick={saveSettings} disabled={savingSettings} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium disabled:opacity-50" data-testid="save-payment-settings">
            <Save className="w-4 h-4" /> {savingSettings ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
          </button>
        </div>
      </section>

      {/* Bank Accounts */}
      <section className="p-6 rounded-2xl bg-card border border-border space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-[hsl(var(--gold))]" />
            <h2 className="font-heading text-lg font-semibold">Banka Hesapları (Havale/EFT)</h2>
          </div>
          <button onClick={() => { resetBank(); setShowBankForm(true); }} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium" data-testid="bank-add-btn">
            <Plus className="w-4 h-4" /> Hesap Ekle
          </button>
        </div>

        {showBankForm && (
          <form onSubmit={submitBank} className="p-4 rounded-xl border border-border bg-background space-y-4" data-testid="bank-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1.5">Banka Adı *</label><input value={bankForm.bank_name} onChange={e => setBankForm(f => ({ ...f, bank_name: e.target.value }))} className={inputCls} data-testid="bank-name" /></div>
              <div><label className="block text-sm font-medium mb-1.5">Hesap Sahibi</label><input value={bankForm.account_name} onChange={e => setBankForm(f => ({ ...f, account_name: e.target.value }))} className={inputCls} data-testid="bank-account-name" /></div>
              <div className="sm:col-span-2"><label className="block text-sm font-medium mb-1.5">IBAN *</label><input value={bankForm.iban} onChange={e => setBankForm(f => ({ ...f, iban: e.target.value }))} className={inputCls} placeholder="TR.." data-testid="bank-iban" /></div>
              <div><label className="block text-sm font-medium mb-1.5">Şube</label><input value={bankForm.branch} onChange={e => setBankForm(f => ({ ...f, branch: e.target.value }))} className={inputCls} /></div>
              <div><label className="block text-sm font-medium mb-1.5">Sıra</label><input type="number" value={bankForm.position} onChange={e => setBankForm(f => ({ ...f, position: e.target.value }))} className={inputCls} /></div>
              <div className="sm:col-span-2"><label className="block text-sm font-medium mb-1.5">Açıklama</label><input value={bankForm.description} onChange={e => setBankForm(f => ({ ...f, description: e.target.value }))} className={inputCls} placeholder="Açıklamaya ödeme numarası yazılmasını isteyin." /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={bankForm.is_active} onChange={e => setBankForm(f => ({ ...f, is_active: e.target.checked }))} className="rounded" /> Aktif</label>
            <div className="flex gap-3">
              <button type="submit" disabled={bankSubmitting} className="px-5 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium disabled:opacity-50" data-testid="bank-save">{bankSubmitting ? 'Kaydediliyor...' : 'Kaydet'}</button>
              <button type="button" onClick={resetBank} className="px-5 py-2.5 rounded-lg border border-input text-sm hover:bg-secondary">İptal</button>
            </div>
          </form>
        )}

        {loadingBanks ? (
          <div className="flex justify-center py-6"><div className="w-6 h-6 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
        ) : banks.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Henüz banka hesabı eklenmedi.</p>
        ) : (
          <div className="space-y-2" data-testid="bank-list">
            {banks.map(b => {
              const active = b.is_active === true || b.is_active === 'true' || b.is_active === 'True';
              return (
                <div key={b.id} className="flex items-center gap-3 p-3 rounded-lg border border-border" data-testid="bank-item">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{b.bank_name}</span>
                      {!active && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-destructive/10 text-destructive">Pasif</span>}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono mt-0.5 break-all">{b.iban}</div>
                    {b.account_name && <div className="text-xs text-muted-foreground">{b.account_name}</div>}
                  </div>
                  <button onClick={() => editBank(b)} className="p-2 rounded-lg hover:bg-secondary"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => deleteBank(b.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
